import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IConstantsResponse, IConstantsResponseItems, IObj } from '@modules/sanna/data/models/constants.interface';
import { IDiagnosticos, IDiagnosticosDatosItem } from '@shared/interfaces/diagnosticos-api.interface';
import { IResponseAPI, IResponsePutAPI, IResponseUpdateAPI } from '@shared/interfaces/response-api.interface';

import {
  ILS,
  ILSFullResponse,
  ILSResponse,
  IC2Response,
  ISearchFilter,
  ILSValidationAdmin,
  ISannaLicenseNoCode,
  ISannaLicenseWithCode,
  ILSENTITY,
} from '@sanna/data/models/sanna.interfaces';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IAPISusesoIdParams,
  IAPIScanSaveParam,
  IAPISendNotificationWithAntecedentsBody,
  IAPISendNotificationRequestAntecedents,
  IAPISendNotificationDictamen,
  ICrearFormularioEmpleadorLicenciaBody,
  ILicenciaObtenerItem,
  PresidentVerdict,
  ValidationRecord,
} from '../data/models/sanna-licencia-api.interface';
import { IContingencias, IContingenciasItem } from '../data/models/sanna-contingencias-api.interface';
import { abstractResolutionConsultationResponseItem } from '../interface/abstract.interface';

const URL = environment.apiBaseUrl;

@Injectable({
  providedIn: 'root',
})
export class SannaService {
  private diagnosticURL = {
    cloud: environment.apiBaseUrl + 'diagnostico/v1/listar-diagnosticos',
    local: 'assets/CIES10Procesado.json',
  };

  constructor(private http: HttpClient) { }

  getListLicencesToRecepcionist(params?: ISearchFilter): Observable<ILS[]> {
    const qs = new HttpParams({ fromObject: params as any });
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista`, { params: qs })
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de licencias SANNA en estado recepcionado.
   */
  getListLicencesToDigitaze(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-recepcion/v1/licencias/digitalizador`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de licencias SANNA para ser Digitado
   */
  getListLicencesToType(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-recepcion/v1/licencias/digitador`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de parámetros "constantes"
   */
  getLicenseConstants(): Observable<IConstantsResponseItems[]> {
    return this.http
      .get<IConstantsResponse>(`${URL}/sanna-ciudadano/v1/parametros`)
      .pipe(map((response) => response.items));
  }

  /***
   *
   */
  getLicense(folio: string): Observable<ILicenciaObtenerItem[]> {
    return this.http
      .get<ILSResponse>(`${URL}/sanna-ciudadano/v1/licencias/${folio}`)
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene TODO el objeto de la licencia médica (y no solo sus Items)
   *
   * @param folio Numero de folio de la licencia
   */
  getLicenseFull(folio: string): Observable<ILSFullResponse> {
    return this.http
      .get<ILSFullResponse>(`${URL}/sanna-ciudadano/v1/licencias/${folio}`)
      .pipe(map((response) => response));
  }

  /**
   *
   * @param rut rut del trabajador
   * @param folio folio de la licencia Sanna
   */
  getIC1(rut: string, folio: string): Observable<any> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-common/v1/licencias/ic1/${folio}?trabajadorRut=${rut}`)
      .pipe(map((response) => response.items[0] || null));
  }

  /**
   *
   * @param rut rut del trabajador
   * @param folio folio de la licencia Sanna
   */
  getIC2(rut: string, folio: string): Observable<IC2Response> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-common/v1/licencias/ic2/${folio}?trabajadorRut=${rut}`)
      .pipe(map((response) => response.items[0] || null));
  }

  /**
   * Obtiene los diagnosticos desde un endpoint
   */
  getDiagnostic(): Observable<IDiagnosticosDatosItem[]> {
    return this.http.get<IDiagnosticos>(this.diagnosticURL.cloud).pipe(map((response) => response.datos.Items));
  }

  /**
   * Obtiene los diagnosticos desde un json
   */
  getLocalDiagnostic(): Observable<IDiagnosticosDatosItem[]> {
    return this.http.get<IDiagnosticosDatosItem[]>(this.diagnosticURL.local);
  }

  /**
   * Obtiene lista de contingencias usadas en licencia Sanna
   */
  getContingencies(): Observable<IContingenciasItem[]> {
    return this.http
      .get<IContingencias>(`${URL}/mantenedores-se/v1/contingencias`)
      .pipe(map((response) => response.items));
  }

  /**
   * Crea licencia Sanna que NO tiene código (recepcionista) - envío
   */
  createSannaLicenseNoCodeSend(sannaLicenseNoCode: ISannaLicenseNoCode): Observable<IResponsePutAPI> {
    return this.http.post<IResponsePutAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista/enviar/`,
      sannaLicenseNoCode,
    );
  }

  /**
   * Crea licencia Sanna que NO tiene código (recepcionista) - devolución
   */
  createSannaLicenseNoCodeReturn(sannaLicenseNoCode: ISannaLicenseNoCode): Observable<IResponsePutAPI> {
    return this.http.post<IResponsePutAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista/devolver/`,
      sannaLicenseNoCode,
    );
  }

  /**
   * Registra ID de licencia asignado por SUSESO en LMS
   * @param IAPISusesoIdParams params
   * @param string folio licencia médica SANNA
   */
  setIdLicenciaSuseso(params: IAPISusesoIdParams, folio: string): Observable<IResponseAPI> {
    return this.http.put<IResponseAPI>(`${URL}/sanna-ciudadano/v1/licencias/suseso/zona0/` + folio, params);
  }

  /**
   * Entrga listado de documentos previamente adjuntados
   * @param folio - Folio de la licencia a consultar
   */
  getAntecedentes(folio: string): Observable<IObj[]> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-recepcion/v1/licencias/antecedentes/${folio}`)
      .pipe(map((response) => response.items));
  }

  /**
   * Actualiza licencia Sanna que SI tiene código (recepcionista)
   */
  updateSannaLicenseWithCode(sannaLicenseWithCode: ISannaLicenseWithCode): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(sannaLicenseWithCode.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista/enviar/${folio}`,
      sannaLicenseWithCode,
    );
  }

  /**
   * Actualiza (Devuelve) licencia Sanna que SI tiene código (recepcionista)
   */
  updateSannaLicenseReturn(sannaLicenseWithCode: ISannaLicenseWithCode): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(sannaLicenseWithCode.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista/devolver/${folio}`,
      sannaLicenseWithCode,
    );
  }

  /**
   * Actualiza (re dictamina) licencia Sanna que tiene resolución y dictamen firmado
   * todo: confirmar ruta de actualización
   */
  updateSannaLicenseReverdict(params: any): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/iniciar/redictamen/${folio}`,
      params,
    );
  }

  /**
   * Actualiza (re dictamina) licencia Sanna que tiene resolución y dictamen firmado
   * todo: confirmar ruta de actualización
   */
  updateSannaLicenseReplenishmentResource(params: any): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/iniciar/reposicion/${folio}`,
      params,
    );
  }

  /**
   * Registra documentos solicitados en recepcion
   */
  updateSannaLicenseRecepcionDocumentos(params: any): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/recepcion/solicitud/${folio}`,
      params,
    );
  }

  /**
   *  Actualiza licencia Sanna (digitador)
   * @param sannaLicense datos de la licencia actualizada.
   */
  updateSannaLicense(sannaLicense: ILicenciaObtenerItem): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(sannaLicense.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/digitador/${folio}`,
      sannaLicense,
    );
  }

  scanSave(params: IAPIScanSaveParam): Observable<IResponseUpdateAPI> {
    const licencia_folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/digitalizador/update/${licencia_folio}`,
      params,
    );
  }

  scanSend(params: IAPIScanSaveParam): Observable<IResponseUpdateAPI> {
    const licencia_folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/digitalizador/send/${licencia_folio}`,
      params,
    );
  }

  scanRequestedSend(params: IAPIScanSaveParam): Observable<IResponseUpdateAPI> {
    const licencia_folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-recepcion/v1/licencias/digitalizador/solicitud/${licencia_folio}`,
      params,
    );
  }

  /**
   * Envía correo electrónico con listado de notificaciones (sin archivo adjunto)
   * @param params
   */
  sendNotificationWithAntecedentsSend(params: IAPISendNotificationWithAntecedentsBody): Observable<IResponseUpdateAPI> {
    if (!params.subject) params.subject = 'Aviso de notificación de recepción de antecedentes';
    return this.http.post<IResponsePutAPI>(`${URL}/notificaciones/v1/correo-sanna-enviar`, params);
  }

  /**
   * Envía correo electrónico con listado de notificaciones (sin adjunto)
   * @param params
   */
  sendNotificationWithAntecedentsReturn(
    params: IAPISendNotificationWithAntecedentsBody,
  ): Observable<IResponseUpdateAPI> {
    if (!params.subject) params.subject = 'Aviso de notificación de recepción de antecedentes con devolución';
    return this.http.post<IResponsePutAPI>(`${URL}/notificaciones/v1/correo-sanna-devolucion`, params);
  }

  /**
   * Envía correo electrónico a empleadores o trabajador.
   * Servicio de envío de notificación por correo electrónico de solicitud de documentos adicionales a LM SANNA.
   * @param params
   */
  sendNotificationRequestAntecedents(params: IAPISendNotificationRequestAntecedents): Observable<IResponseUpdateAPI> {
    if (!params.subject) params.subject = 'Aviso de notificación de solicitud de antecedentes';
    return this.http.post<IResponsePutAPI>(`${URL}/notificaciones/v1/correo-sanna-solicitud`, params);
  }

  /**
   * Envía correo electrónico a entidad pagadora.
   * Servicio de envío de notificación por correo electrónico de dictamen firmado de una LM SANNA.
   *
   * @param params
   */
  sendNotificationEntidadPagadora(params: IAPISendNotificationDictamen): Observable<IResponseAPI> {
    return this.http.post<IResponseAPI>(`${URL}/notificaciones/v1/correo-sanna-entidad-pagadora`, params);
  }

  /**
   * Envía correo electrónico a trabajador.
   * Servicio de envío de notificación por correo electrónico de dictamen firmado de una LM SANNA.
   *
   * @param params
   */
  sendNotificationDictamen(params: IAPISendNotificationDictamen): Observable<IResponseAPI> {
    if (!params.subject) params.subject = 'Aviso de notificación de resolución de LM SANNA';
    if (!params.text) {
      params.text =
        `Junto con saludar, le informamos que hemos adjuntado en este correo el ` +
        `dictamen de su licencia médica SANNA con Folio Nº ${params.licencia}.` +
        `El detalle de su licencia médica lo puede revisar en la página milicenciamedica.cl.`;
    }
    return this.http.post<IResponseAPI>(`${URL}/notificaciones/v1/correo-generico-sanna`, params);
  }

  /**
   * Obtiene lista de licencia Sanna para Validación Administrativa
   */
  getValidationAdmin(subcomisionesIds: number[]): Observable<ILSValidationAdmin[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-validacion/v1/licencias/validacion-administrativa/`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene un listado de licencias sanna para ser validadas por usuario FSIL
   */
  getListLicencesValidationRecords(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-validacion/v1/licencias/validacion-antecedentes`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(
        map((response) => {
          return response.items;
        }),
      );
  }

  /**
   * Validación Administrativa
   * Envía datos para cuando todos los documentos han sido Aprobados
   */
  validationAdminApprove(params: any): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-validacion/v1/licencias/validacion-administrativa/aprobar/${folio}`,
      params,
    );
  }

  /**
   * Validación Administrativa
   * Envía datos para cuando alguno de los documentos ha sido Rechazado
   */
  validationAdminRequest(params: any): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(params.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-validacion/v1/licencias/validacion-administrativa/solicitar/${folio}`,
      params,
    );
  }

  /**
   * envía una licencia Sanna en validación de antecedentes
   * @param validationRecord datos de licencia y motivo de rechazo.
   * @deprecated
   */
  sendValidationRecord(validationRecord: ValidationRecord): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(validationRecord.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-validacion/v1/licencias/validacion-antecedentes/enviar/${folio}`,
      validationRecord,
    );
  }

  /**
   * Listado de LM del prefil Funcionario/a Apoyo al Contralor
   */
  getValidationComptrollerSupport(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-validacion/v1/licencias/apoyo-contralor`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  sendValidationComptrollerSupport(validationRecord: ValidationRecord): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(validationRecord.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-validacion/v1/licencias/apoyo-contralor/${folio}`,
      validationRecord,
    );
  }

  /**
   * Listado de LM del perfil Contralor
   */
  getValidationComptroller(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-resolucion/v1/licencias/contralor`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  /**
   * Listado de LM del perfil Presidente
   */
  getValidationPresident(subcomisionesIds: number[]): Observable<ILS[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-escritorio-resolucion/v1/licencias/presidente`, {
        subcomisiones: subcomisionesIds,
      })
      .pipe(map((response) => response.items));
  }

  /**
   * Devuelve una licencia Sanna en Dictamen Presidente.
   */
  returnToComptroller(validationRecord: PresidentVerdict): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(validationRecord.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-resolucion/v1/licencias/presidente/devolver/${folio}`,
      validationRecord,
    );
  }

  /***
   * Rechaza una licencia Sanna en Dictamen Presidente.
   */
  rejectFromPresident(validationRecord: PresidentVerdict): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(validationRecord.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-resolucion/v1/licencias/presidente/rechazar/${folio}`,
      validationRecord,
    );
  }

  /***
   * Aprueba una licencia Sanna en Dictamen Presidente.
   */
  approveFromPresident(validationRecord: PresidentVerdict): Observable<IResponseUpdateAPI> {
    const folio = encodeURIComponent(validationRecord.licencia_folio);
    return this.http.put<IResponseUpdateAPI>(
      `${URL}/sanna-escritorio-resolucion/v1/licencias/presidente/aprobar/${folio}`,
      validationRecord,
    );
  }

  getCartola(): Observable<ILS[]> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-recepcion/v1/licencias/recepcionista`)
      .pipe(map((response) => response.items));
  }

  getZoneC(folio: string): Observable<ICrearFormularioEmpleadorLicenciaBody> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-common/v1/licencias/${folio}/seccion-c`)
      .pipe(map((response) => response.items[0] || null));
  }

  /**
   * Obtiene listado de licencias SANNA para Referente DCN.
   */
  getListLicencesToDcnReferrer(): Observable<ILS[]> {
    return this.http
      .get<IResponseAPI>(`${URL}/sanna-escritorio-common/v1/licencias/referente-dcn`, {})
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de licencias SANNA para Entidad Pagadora
   */
  getListLicencesToPayingEntityConsultation(params: any): Observable<ILSENTITY[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-ext/v1/entidad/consulta`, params)
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de licencias SANNA para Entidad Pagadora para exportar el excel
   */
  getListLicencesToExportExcel(params: any): Observable<ILSENTITY[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-ext/v1/entidad/consulta-excel`, params)
      .pipe(map((response) => response.items));
  }

  /**
   * Obtiene listado de licencias SANNA para Entidad Pagadora
   */
  getListLicencesToPayingEntityProcessing(params: any): Observable<ILSENTITY[]> {
    return this.http
      .post<IResponseAPI>(`${URL}/sanna-ext/v1/entidad/tramite`, params)
      .pipe(map((response) => response.items));
  }

  /**
   * Comprueba si existe un folio con la licencia médica electronica especificada
   * @param lme
   */
  checkLME(lme: string, trabajadorRut: string): Observable<any> {
    const params = {
      licenciaFolioElectronica: lme,
      trabajadorRut: trabajadorRut
    }
    return this.http.post<any>(`${URL}/notificacion-lme/v1/licencia/lme/lme-validation`, params);
  }

  
  /**
   * Actualiza documentos entidad Pagadora
   * @param {abstractResolutionConsultationResponseItem} data 
   * @returns {Observable<IResponseUpdateAPI>}
   */
  updateFilesEntidad(data: abstractResolutionConsultationResponseItem): Observable<IResponseUpdateAPI> {
    return this.http.put<IResponseUpdateAPI>(`${URL}/sanna-escritorio-resolucion/v1/licencias/entidad-update`, data);
  }

}
